import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Rule238Service } from 'src/app/_services/rule238/rule238.service';
import { DownloadFile, Rule238, Rule238Update } from 'src/app/interfaces/rule238.interfaces';

/**
 * Componente de formulario para capturar y editar los datos de la Regla 2.3.8,
 * incluyendo el arreglo dinámico de mercancía embargada con sus archivos
 * adjuntos, y emite eventos para guardar, actualizar, eliminar o regresar.
 */
@Component({
  selector: 'app-rule238-form',
  templateUrl: './rule238-form.component.html',
  styleUrls: ['./rule238-form.component.css']
})
export class Rule238FormComponent implements OnInit, OnChanges {
  @Input() canDelete: boolean = false;
  @Input() rule238Update?: Rule238Update;
  @Input() wasCreatedUpdated: boolean = false;
  @Output() goback = new EventEmitter<void>();
  @Output() save = new EventEmitter<Rule238>();
  @Output() update = new EventEmitter<Rule238>();
  @Output() delete = new EventEmitter<void>();
  rule238Form: FormGroup;
  filesUrl: string[] = [];
  errorMessageFile: string = 'Ingrese el archivo';

  constructor(
    private fb: FormBuilder,
    private rule238Svc: Rule238Service,
  ) {
    this.rule238Form = this.fb.group({
      pama_number: [, [Validators.maxLength(150)]],
      fiscal_folio_cfdi: [, [Validators.maxLength(150)]],
      total_amount_cfdi: [, [Validators.maxLength(150)]],
      total_amount_presentation: [, [Validators.maxLength(150)]],
      amount_merchandise_deposit: [, [Validators.maxLength(150)]],
      compensation_amount: [, [Validators.maxLength(150)]],
      damage_violation_record: [, [Validators.maxLength(50)]],
      customs_embargoed_merchandise: this.fb.array([]),
      identification_stored_merchandise: [, [Validators.maxLength(100)]],
      volume_occupied_merchandise: [, [Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      container_dimension: [, [Validators.maxLength(150)]],
      container_type: [, [Validators.maxLength(150)]],
      number_container_seals: [, [Validators.maxLength(150)]],
      number_container_locks: [, [Validators.maxLength(150)]],
      unconsolidated_container: [, [Validators.maxLength(150)]],
      deconsolidated_store: [, [Validators.maxLength(150)]],
      unconsolidated_means_transport: [, [Validators.maxLength(150)]],
      consolidated_container: [, [Validators.maxLength(150)]],
      consolidated_store: [, [Validators.maxLength(150)]],
      consolidated_means_transport: [, [Validators.maxLength(150)]],
    });
  }

  get customs_embargoed_merchandiseArr() {
    return this.rule238Form.get('customs_embargoed_merchandise') as FormArray;
  }

  /**
   * Reconstruye el arreglo de mercancía embargada y rellena el formulario cuando llega un registro de Regla 2.3.8 para editar.
   * @param changes - Cambios detectados en las propiedades de entrada del componente.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rule238Update'] && changes['rule238Update'].currentValue && changes['rule238Update'].currentValue.id) {
      this.filesUrl = [];
      const currentEbargoedMerchandis = this.rule238Update?.customs_embargoed_merchandise || [];
      this.customs_embargoed_merchandiseArr.clear();
      currentEbargoedMerchandis.forEach((customsEmbargoedMerchandise: any, index: number) => {
        this.addFormGroup(false);
        const ids: DownloadFile = {
          id: customsEmbargoedMerchandise.id,
          rule_register_id: this.rule238Update?.id || 0
        };
        this.rule238Svc.downloadEmbargoedFile(ids)
        .subscribe({
          next: (response) => {
            if (!response.error) {
              this.filesUrl.splice(index, 0, response.url);
            }
          }
        });
      });
      this.rule238Form.reset({...this.rule238Update});
    }
  }

  ngOnInit(): void {
  }

  /**
   * Valida el formulario y emite el evento de guardado o actualización con los datos de la Regla 2.3.8.
   */
  saveRule238() {
    if (this.rule238Form.invalid) {
      this.rule238Form.markAllAsTouched();
      return;
    }
    const rule238: Rule238 = {
      ...this.rule238Form.value,
      volume_occupied_merchandise: Number(this.rule238Form.get('volume_occupied_merchandise')?.value),
    };
    rule238.customs_embargoed_merchandise = rule238.customs_embargoed_merchandise.map((customsEmbargoedMerchandise, index) => {
      return {
        ...customsEmbargoedMerchandise,
        weight_embargoed_merchandise: Number(customsEmbargoedMerchandise.weight_embargoed_merchandise),
      }
    });
    if (this.canDelete)
      this.update.emit(rule238);
    else
      this.save.emit(rule238);
  }

  /**
   * Emite el evento para regresar al detalle del barco.
   */
  goBackShipDetails() {
    this.goback.emit();
  }

  /**
   * Emite el evento para eliminar la Regla 2.3.8.
   */
  deleteRule238() {
    this.delete.emit();
  }

  /**
   * Reinicia el formulario, vacía el arreglo de mercancía embargada y limpia los archivos.
   */
  resetForm() {
    this.rule238Form.reset();
    this.customs_embargoed_merchandiseArr.clear();
    this.filesUrl = [];
  }

  /**
   * Indica si un campo del formulario es inválido y ha sido tocado.
   * @param field - Nombre del campo a evaluar.
   * @returns `true` si el campo tiene errores y fue tocado; en caso contrario `null` o `false`.
   */
  isValidField( field: string): boolean | null {
    return this.rule238Form.get(field)!.errors &&
    this.rule238Form.get(field)!.touched;
  }

  /**
   * Indica si un campo dentro de un arreglo del formulario es inválido y ha sido tocado.
   * @param arrayName - Nombre del arreglo de formularios.
   * @param index - Índice del elemento dentro del arreglo.
   * @param fieldName - Nombre del campo a evaluar.
   * @returns `true` si el campo tiene errores y fue tocado; en caso contrario `null` o `false`.
   */
  isValidFieldInArray( arrayName: string, index: number, fieldName: string): boolean | null {
    return this.rule238Form.get(`${arrayName}.${index}.${fieldName}`)!.errors && this.rule238Form.get(`${arrayName}.${index}.${fieldName}`)!.touched
  }

  /**
   * Procesa el archivo seleccionado para una mercancía embargada, lo valida por tamaño y lo convierte a base64.
   * @param event - Evento del input de archivo.
   * @param index - Índice del elemento de mercancía embargada en el arreglo.
   * @param campo - Nombre del campo asociado al archivo.
   */
  fileInput(event: any, index: number, campo: string): void {
    if (event.target.files[0]) {
      if (event.target.files[0].size > 10 * 1024 * 1024) {
        this.errorMessageFile = 'El archivo tiene que ser menos a 10mb';
        this.rule238Form.get(`customs_embargoed_merchandise.${index}.file`)?.setValue('');
        return;
      }
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const cadena = reader.result?.toString() || '';
        const contenidoBase64 = cadena.split(',')[1];
        this.rule238Form.get(`customs_embargoed_merchandise.${index}.file`)?.setValue(contenidoBase64);
      }
    } else {
      this.errorMessageFile = 'Ingrese el archivo';
      this.rule238Form.get(`customs_embargoed_merchandise.${index}.file`)?.setValue('');
    }
  }

  /**
   * Agrega al arreglo un nuevo grupo de formulario de mercancía embargada.
   * @param isFileRequired - `true` para marcar el archivo como obligatorio en el nuevo grupo.
   */
  addFormGroup(isFileRequired: boolean): void {
    let customsEmbargoedMerchandise = this.fb.group({
      id: [],
      trade_number: [, [Validators.required, Validators.maxLength(20), Validators.pattern(/^[0-9]\d{0,29}$/)]],
      office_date: [, [Validators.required]],
      weight_embargoed_merchandise: [, [Validators.required, Validators.pattern(/^\d+(\.\d{1,3})?$/)]],
      file: [],
    })
    if (isFileRequired) {
      customsEmbargoedMerchandise.get('file')?.setValidators([Validators.required]);
      customsEmbargoedMerchandise.get('file')?.updateValueAndValidity();
    }
    this.customs_embargoed_merchandiseArr.push(customsEmbargoedMerchandise);
  }

  /**
   * Elimina el último grupo de mercancía embargada del arreglo y su archivo asociado si corresponde.
   */
  removeFormGroup(): void {
    const index: number = this.customs_embargoed_merchandiseArr.length - 1;
    this.customs_embargoed_merchandiseArr.removeAt(index);
    // Si el lengt del array es el mismo significa que se elimino un elemento con archivo
    // por lo tanto se elimina el ultimo elemento del array de archivos
    if (index + 1 === this.filesUrl.length) {
      this.filesUrl.pop();
    }
  }
}
